import React, { useState } from 'react';
import { IconButton } from '../../../../../../../../components/IconButton';
import { Application } from '../../../../../../../../entities/Application';
import { RejectCandidateStageModal } from '../../../../../../../CandidatePage/CandidateActions/RejectCandidate';
import {
  AlertObject,
  AlertType,
} from '../../../../../../../../components/Alert';
import { ConflictError } from '../../../../../../../../services/ApiService/errors/ConflictError';
import { ApiError } from '../../../../../../../../services/ApiService/errors/ApiError';
import { BULK_REVIEW_NAVIGATION_TYPE } from '../../../../..';
import { User } from '../../../../../../../../entities/User';
import { AccountIntegration } from '../../../../../../../../entities/AccountIntegration';
import { SendEmailModal } from '../../../../../../../SendEmailModal';
import { ApplicationService } from '../../../../../../../../services/applicant_tracking/ApplicationService';
import { SendEmailDto } from '../../../../../../../../services/dtos/EmailDto';

interface PropTypes {
  application: Application;
  preSelectedRejectReasonId: number;
  disabled: boolean;
  currentUser: User;
  emailAccountIntegration: AccountIntegration;
  setAlert: (alert: AlertObject) => void;
  onNext: (navType: BULK_REVIEW_NAVIGATION_TYPE) => void;
  onConflict: () => void;
  setPreSelectedRejectReasonId: (id: number) => void;
}

export function RejectApplication(props: PropTypes) {
  const [isRejectModalOpen, setIsRejectModalOpen] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [isSendEmailModalOpen, setIsSendEmailModalOpen] =
    useState<boolean>(false);

  const handleRejectModalClose = (
    succeeded: boolean,
    rejectedReason?: string,
    error?: Error,
    hasRejectionEmailAutomation?: boolean,
  ) => {
    if (succeeded === false) {
      setIsRejectModalOpen(false);

      if (error instanceof ConflictError) {
        props.setAlert({
          type: AlertType.Danger,
          message: <span className='fw-semibold'>{error.body?.message}</span>,
        });
        props.onConflict();
      } else {
        props.setAlert({
          type: AlertType.Danger,
          message: (
            <span className='fw-semibold'>
              Candidate could not be rejected with the reason &quot;
              {rejectedReason}
              &quot;.
            </span>
          ),
        });
      }

      return;
    }

    if (succeeded !== true) {
      setIsRejectModalOpen(false);
      return;
    }

    props.setAlert({
      type: AlertType.Success,
      message: (
        <span className='fw-semibold'>
          Candidate rejected with the reason &quot;{rejectedReason}&quot;.
        </span>
      ),
    });

    const shouldShowEmailModal =
      props.emailAccountIntegration.enabled &&
      props.currentUser.hasEmailConnected &&
      !hasRejectionEmailAutomation;

    if (shouldShowEmailModal) {
      setShowConfirmationModal(true);
    } else {
      setIsRejectModalOpen(false);
      props.onNext('rejected');
    }
  };

  const handleClose = (sendEmail: boolean) => {
    setIsRejectModalOpen(false);
    setShowConfirmationModal(false);

    if (sendEmail) {
      setIsSendEmailModalOpen(true);
    } else {
      props.onNext('rejected');
    }
  };

  const onSendEmail = async (sendEmailProps: SendEmailDto) => {
    try {
      await ApplicationService.sendEmails(
        [props.application.id],
        sendEmailProps,
      );
      props.setAlert({
        type: AlertType.Success,
        message: 'The email was successfully sent.',
      });
    } catch (e) {
      const error = e as ApiError;
      props.setAlert({
        type: AlertType.Danger,
        message: error.body?.message || 'The email was not sent.',
      });
    } finally {
      setIsSendEmailModalOpen(false);
      props.onNext('rejected');
    }
  };

  return (
    <>
      <RejectCandidateStageModal
        applicationId={props.application.id}
        candidateName={props.application.candidate.name}
        jobName={props.application.job?.name}
        isOpen={isRejectModalOpen}
        showConfirmationModal={showConfirmationModal}
        filterRejectedBy='organization'
        preSelectedRejectReasonId={props.preSelectedRejectReasonId}
        currentStageId={props.application.currentJobStageId}
        onClose={handleClose}
        setPreSelectedRejectReasonId={props.setPreSelectedRejectReasonId}
        onReject={handleRejectModalClose}
      />
      {isSendEmailModalOpen && (
        <SendEmailModal
          applicationIds={[props.application.id]}
          toEmail={props.application.candidate.email}
          hasEmailConnected={props.currentUser.hasEmailConnected}
          isOpen={isSendEmailModalOpen}
          onCloseModal={() => {
            setIsSendEmailModalOpen(false);
            props.onNext('rejected');
          }}
          onSendEmail={onSendEmail}
        />
      )}
      <IconButton
        iconName='bi-x-circle'
        buttonText='Reject'
        buttonClass='me-2h'
        color='danger'
        disabled={props.disabled}
        onClick={() => setIsRejectModalOpen(true)}
      />
    </>
  );
}
